<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { adminApi } from '../api/admin.api';
import { useAuthStore } from '../stores/auth.store';

/** Displaro staff on a "view as customer" visit: always visible, with the time left and a way out. */
const { t } = useI18n();
const auth = useAuthStore();
const visit = computed(() => auth.activeOrganization?.support ?? null);
const now = ref(Date.now());
const timer = setInterval(() => (now.value = Date.now()), 1000);
onBeforeUnmount(() => clearInterval(timer));
const left = computed(() => (visit.value ? Math.max(0, Date.parse(visit.value.until) - now.value) : 0));
const clock = computed(() => {
  const s = Math.ceil(left.value / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
});
const busy = ref(false);
async function exit(): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  try {
    await adminApi.endSupport().catch(() => undefined);
    // Back to the platform admin app (its own app at /admin).
    window.location.assign('/admin/#/organizations');
  } finally {
    busy.value = false;
  }
}
// Time's up: the server has closed it already; come back to the admin area.
watch(left, (ms) => visit.value && ms <= 0 && void exit());
</script>

<template>
  <div v-if="visit" class="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-red-700 bg-red-600 px-4 py-2 text-sm text-white" role="status">
    <strong>{{ t('admin.visiting', { org: auth.activeOrganization?.name ?? '' }) }}</strong>
    <span class="opacity-90" :title="visit.reason">{{ t('admin.visitLeft', { time: clock, role: auth.activeOrganization?.role ?? '' }) }}</span>
    <button type="button" class="rounded bg-white/15 px-2.5 py-0.5 font-medium hover:bg-white/25" :disabled="busy" @click="exit">{{ t('admin.exitVisit') }}</button>
  </div>
</template>
